import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiGet } from '../utils/api';
import { isEnterpriseModuleEnabled } from '../utils/fieldVisitAccess.js';
import { useAuth } from './AuthContext';

const SiteContext = createContext();

/** JWT / localStorage peuvent stocker permissions en string JSON */
function normalizePermissions(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p !== null && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

const ENTREPRISE_MODULES_SYNC_KEY = 'secura:entreprise-modules-sync';

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [multiSites, setMultiSites] = useState(false);
  const [pointageInterSiteEnabled, setPointageInterSiteEnabled] = useState(false);
  const [pointageTerrainEnabled, setPointageTerrainEnabled] = useState(false);
  const [paieEnabled, setPaieEnabled] = useState(false);
  const [comptabilitePaieEnabled, setComptabilitePaieEnabled] = useState(false);
  const [salaryAdvancesEnabled, setSalaryAdvancesEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [siteChanging, setSiteChanging] = useState(false); // État pour le skeleton lors du changement de site
  const { token, user, loading: authLoading } = useAuth();

  // Centraliser la logique de détection "Gestion Site" avec site assigné
  const userPermissions = normalizePermissions(user?.permissions);
  const hasSiteManagement = userPermissions.can_manage_site && userPermissions.assigned_site_id;
  const assignedSiteId = hasSiteManagement ? parseInt(userPermissions.assigned_site_id) : null;
  
  // Vérifier si l'utilisateur est AdminRH (pas AdminRH+1)
  const userRole = user?.role?.nom || user?.roles?.[0]?.nom || user?.role || '';
  const isAdminRH = userRole === 'AdminRH' || userRole === 'RH-Admin' || userRole === 'DRH';
  const isAdminRHPlus = userRole === 'AdminRH+1' || userRole === 'RH+1' || userRole === 'Manager';
  
  // Seul AdminRH (sans site assigné) peut voir "Généralisé"
  const canSeeGeneralized = isAdminRH && !hasSiteManagement;

  // Charger les sites et vérifier si multi-sites est activé
  useEffect(() => {
    // Attendre que l'authentification soit vérifiée
    if (authLoading) {
      return;
    }

    // Ne charger les sites que si l'utilisateur est authentifié
    if (!token || !user) {
      setSites([]);
      setSelectedSite(null);
      setMultiSites(false);
      setPointageInterSiteEnabled(false);
      setPointageTerrainEnabled(false);
      setPaieEnabled(false);
      setComptabilitePaieEnabled(false);
      setSalaryAdvancesEnabled(false);
      setLoading(false);
      return;
    }

    const loadSites = async () => {
      try {
        if (hasSiteManagement) {
          try {
            const settingsQuick = await apiGet('/api/entreprise/settings', { silent: true });
            if (settingsQuick.success && settingsQuick.settings) {
              setPointageInterSiteEnabled(
                isEnterpriseModuleEnabled(settingsQuick.settings.pointage_inter_site_enabled)
              );
              setPointageTerrainEnabled(
                isEnterpriseModuleEnabled(settingsQuick.settings.pointage_terrain_enabled)
              );
              setPaieEnabled(isEnterpriseModuleEnabled(settingsQuick.settings.paie_enabled));
              setComptabilitePaieEnabled(isEnterpriseModuleEnabled(settingsQuick.settings.comptabilite_paie_enabled));
              setSalaryAdvancesEnabled(isEnterpriseModuleEnabled(settingsQuick.settings.salary_advances_enabled));
            }
          } catch {
            setPointageInterSiteEnabled(false);
            setPointageTerrainEnabled(false);
          }
          // L'utilisateur a un site assigné, charger uniquement ce site
          // Charger les sites pour trouver le site assigné
          const sitesResponse = await apiGet('/api/entreprise/sites', { silent: true });
          if (sitesResponse.success) {
            const sitesList = sitesResponse.data || [];
            const assignedSite = sitesList.find(s => s.id === assignedSiteId);
            
            if (assignedSite) {
              // Définir multi-sites comme true pour que le système fonctionne correctement
              setMultiSites(true);
              // Ne garder que le site assigné dans la liste
              setSites([assignedSite]);
              // Sélectionner automatiquement le site assigné
              setSelectedSite(assignedSite);
              localStorage.setItem('selectedSiteId', assignedSite.id.toString());
              console.log('✅ Site assigné chargé automatiquement:', assignedSite.nom);
            } else {
              console.error('❌ Site assigné non trouvé:', assignedSiteId);
            }
          }
          setLoading(false);
          return; // Ne pas continuer avec le chargement normal
        }
        
        // Vérifier si l'entreprise utilise multi-sites
        const settingsResponse = await apiGet('/api/entreprise/settings', { silent: true });
        if (settingsResponse.success && settingsResponse.settings) {
          const isMultiSites = isEnterpriseModuleEnabled(settingsResponse.settings.multi_sites);
          setMultiSites(isMultiSites);
          setPointageInterSiteEnabled(
            isEnterpriseModuleEnabled(settingsResponse.settings.pointage_inter_site_enabled)
          );
          setPointageTerrainEnabled(
            isEnterpriseModuleEnabled(settingsResponse.settings.pointage_terrain_enabled)
          );
          setPaieEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.paie_enabled));
          setComptabilitePaieEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.comptabilite_paie_enabled));
          setSalaryAdvancesEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.salary_advances_enabled));

          if (isMultiSites) {
            // Charger les sites
            const sitesResponse = await apiGet('/api/entreprise/sites', { silent: true });
            if (sitesResponse.success) {
              const sitesList = sitesResponse.data || [];
              
              if (hasSiteManagement) {
                // Si l'utilisateur a un site assigné, ne pas ajouter "Généralisé"
                setSites(sitesList);
                
                // Sélectionner le site assigné
                const assignedSite = sitesList.find(s => s.id === assignedSiteId);
                if (assignedSite) {
                  setSelectedSite(assignedSite);
                  localStorage.setItem('selectedSiteId', assignedSite.id.toString());
                }
              } else if (canSeeGeneralized) {
                // Ajouter le site "Généralisé" en premier (seulement pour AdminRH sans site assigné)
                const generalSite = {
                  id: 'generalized',
                  nom: 'Généralisé',
                  est_site_par_defaut: false,
                  actif: true,
                  isGeneralized: true // Flag pour identifier le site généralisé
                };
                
                const sitesWithGeneralized = [generalSite, ...sitesList];
                setSites(sitesWithGeneralized);

                // Sélectionner par défaut le site créé (après activation multi-site) pour voir les données de ce site ; sinon "Généralisé"
                const savedSiteId = localStorage.getItem('selectedSiteId');
                let defaultSite = null;
                
                if (savedSiteId === 'generalized') {
                  defaultSite = generalSite;
                } else if (savedSiteId) {
                  defaultSite = sitesList.find(s => s.id === parseInt(savedSiteId)) || generalSite;
                } else {
                  // Après activation multi-site : un seul site → le sélectionner pour afficher les données de ce site
                  defaultSite = sitesList.length === 1 ? sitesList[0] : generalSite;
                }
                
                if (defaultSite) {
                  setSelectedSite(defaultSite);
                  // Sauvegarder dans localStorage
                  if (defaultSite.id === 'generalized') {
                    localStorage.setItem('selectedSiteId', 'generalized');
                  } else {
                    localStorage.setItem('selectedSiteId', defaultSite.id);
                  }
                }
              } else {
                // AdminRH+1 ou autres rôles sans site assigné : ne pas ajouter "Généralisé"
                setSites(sitesList);
                
                // Sélectionner le premier site ou le site sauvegardé
                const savedSiteId = localStorage.getItem('selectedSiteId');
                if (savedSiteId && savedSiteId !== 'generalized') {
                  const savedSite = sitesList.find(s => s.id === parseInt(savedSiteId));
                  if (savedSite) {
                    setSelectedSite(savedSite);
                  } else if (sitesList.length > 0) {
                    setSelectedSite(sitesList[0]);
                    localStorage.setItem('selectedSiteId', sitesList[0].id.toString());
                  }
                } else if (sitesList.length > 0) {
                  setSelectedSite(sitesList[0]);
                  localStorage.setItem('selectedSiteId', sitesList[0].id.toString());
                }
              }
            }
          } else {
            // Si pas de multi-sites, créer un site virtuel avec les coordonnées de l'entreprise
            setSelectedSite({
              id: null,
              nom: 'Site par défaut',
              latitude: settingsResponse.settings.latitude,
              longitude: settingsResponse.settings.longitude,
              rayon_pointage: settingsResponse.settings.rayon_pointage || 100,
              est_site_par_defaut: true,
              isDefault: true
            });
          }
        }
      } catch (error) {
        // Ne pas afficher d'erreur si c'est juste un problème d'authentification
        // (cela peut se produire avant l'authentification)
        if (error.message && !error.message.includes('Token d\'authentification manquant')) {
          console.error('Erreur lors du chargement des sites:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSites();
  }, [authLoading, token, user, hasSiteManagement, assignedSiteId, canSeeGeneralized]);

  // Restaurer le site sélectionné après le chargement (seulement si pas de site assigné)
  useEffect(() => {
    // Si l'utilisateur a un site assigné, ne pas restaurer depuis localStorage
    if (hasSiteManagement) {
      return;
    }
    
    if (sites.length > 0 && multiSites) {
      const savedSiteId = localStorage.getItem('selectedSiteId');
      if (savedSiteId) {
        let savedSite = null;
        if (savedSiteId === 'generalized') {
          // Vérifier si l'utilisateur peut voir "Généralisé"
          if (canSeeGeneralized) {
            savedSite = sites.find(s => s.id === 'generalized' || s.isGeneralized);
          } else {
            // Si l'utilisateur ne peut pas voir "Généralisé", sélectionner le premier site réel
            const realSites = sites.filter(s => s.id !== 'generalized' && !s.isGeneralized);
            if (realSites.length > 0) {
              savedSite = realSites[0];
              localStorage.setItem('selectedSiteId', savedSite.id.toString());
            }
          }
        } else {
          savedSite = sites.find(s => s.id === parseInt(savedSiteId));
        }
        if (savedSite) {
          setSelectedSite(savedSite);
        } else {
          // Si le site sauvegardé n'existe plus
          if (canSeeGeneralized) {
            // Sélectionner "Généralisé" si l'utilisateur peut le voir
            const generalSite = sites.find(s => s.id === 'generalized' || s.isGeneralized);
            if (generalSite) {
              setSelectedSite(generalSite);
              localStorage.setItem('selectedSiteId', 'generalized');
            }
          } else {
            // Sinon, sélectionner le premier site réel
            const realSites = sites.filter(s => s.id !== 'generalized' && !s.isGeneralized);
            if (realSites.length > 0) {
              setSelectedSite(realSites[0]);
              localStorage.setItem('selectedSiteId', realSites[0].id.toString());
            }
          }
        }
      }
    }
  }, [sites, multiSites, hasSiteManagement, canSeeGeneralized]);

  const selectSite = (site) => {
    // Si l'utilisateur a un site assigné, empêcher le changement de site
    if (hasSiteManagement) {
      // Vérifier si le site sélectionné correspond au site assigné
      if (site && site.id !== assignedSiteId) {
        console.warn('⚠️ Changement de site bloqué: l\'utilisateur a un site assigné');
        return; // Ne pas permettre le changement
      }
    }
    
    // Vérifier si le site change réellement
    const isSiteChanging = selectedSite?.id !== site?.id;
    
    if (isSiteChanging) {
      // Activer le skeleton screen
      setSiteChanging(true);
      
      // Désactiver le skeleton après un court délai (pour permettre le rechargement des données)
      setTimeout(() => {
        setSiteChanging(false);
      }, 500);
    }
    
    setSelectedSite(site);
    if (site && site.id) {
      localStorage.setItem('selectedSiteId', site.id);
    } else {
      localStorage.removeItem('selectedSiteId');
    }
    
    // Déclencher un événement personnalisé pour notifier les composants du changement de site
    window.dispatchEvent(new CustomEvent('siteChanged', { detail: { site } }));
  };

  const refreshSites = async () => {
    // Ne pas rafraîchir si l'utilisateur n'est pas authentifié
    if (!token || !user) {
      return;
    }

    try {
      try {
        const settingsResponse = await apiGet('/api/entreprise/settings', { silent: true });
        if (settingsResponse.success && settingsResponse.settings) {
          setPointageInterSiteEnabled(
            isEnterpriseModuleEnabled(settingsResponse.settings.pointage_inter_site_enabled)
          );
          setPointageTerrainEnabled(
            isEnterpriseModuleEnabled(settingsResponse.settings.pointage_terrain_enabled)
          );
          setPaieEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.paie_enabled));
          setComptabilitePaieEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.comptabilite_paie_enabled));
          setSalaryAdvancesEnabled(isEnterpriseModuleEnabled(settingsResponse.settings.salary_advances_enabled));
          setMultiSites(isEnterpriseModuleEnabled(settingsResponse.settings.multi_sites));
        }
      } catch {
        // ignore
      }

      const sitesResponse = await apiGet('/api/entreprise/sites', { silent: true });
      if (sitesResponse.success) {
        const sitesList = sitesResponse.data || [];
        
        // Utiliser les valeurs centralisées
        if (hasSiteManagement) {
          // Si l'utilisateur a un site assigné, ne pas ajouter "Généralisé"
          setSites(sitesList);
          
          // S'assurer que le site assigné est sélectionné
          const assignedSite = sitesList.find(s => s.id === assignedSiteId);
          if (assignedSite) {
            setSelectedSite(assignedSite);
            localStorage.setItem('selectedSiteId', assignedSite.id.toString());
          }
        } else if (canSeeGeneralized) {
          // Ajouter le site "Généralisé" en premier (seulement pour AdminRH sans site assigné)
          const generalSite = {
            id: 'generalized',
            nom: 'Généralisé',
            est_site_par_defaut: false,
            actif: true,
            isGeneralized: true
          };
          
          const sitesWithGeneralized = [generalSite, ...sitesList];
          setSites(sitesWithGeneralized);

          // Si le site actuellement sélectionné n'existe plus, sélectionner "Généralisé"
          if (selectedSite && selectedSite.id) {
            const currentSite = sitesWithGeneralized.find(s => {
              if (selectedSite.id === 'generalized' || selectedSite.isGeneralized) {
                return s.id === 'generalized' || s.isGeneralized;
              }
              return s.id === selectedSite.id;
            });
            if (!currentSite) {
              selectSite(generalSite);
            }
          }
        } else {
          // AdminRH+1 ou autres rôles : ne pas ajouter "Généralisé"
          setSites(sitesList);
          
          // S'assurer qu'un site est sélectionné
          if (selectedSite && selectedSite.id && selectedSite.id !== 'generalized') {
            const currentSite = sitesList.find(s => s.id === selectedSite.id);
            if (!currentSite && sitesList.length > 0) {
              setSelectedSite(sitesList[0]);
              localStorage.setItem('selectedSiteId', sitesList[0].id.toString());
            }
          } else if (sitesList.length > 0 && !selectedSite) {
            setSelectedSite(sitesList[0]);
            localStorage.setItem('selectedSiteId', sitesList[0].id.toString());
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des sites:', error);
    }
  };

  const refreshSitesRef = useRef(refreshSites);
  refreshSitesRef.current = refreshSites;

  // Après activation des modules dans l’Admin Panel : même onglet (CustomEvent) ou autre onglet (storage)
  useEffect(() => {
    const maybeRefresh = (companyId) => {
      if (companyId == null || !user?.entreprise_id || !token) return;
      if (String(companyId) !== String(user.entreprise_id)) return;
      refreshSitesRef.current();
    };

    const onCustom = (e) => maybeRefresh(e.detail?.companyId);
    const onStorage = (e) => {
      if (e.key !== ENTREPRISE_MODULES_SYNC_KEY || !e.newValue) return;
      try {
        const payload = JSON.parse(e.newValue);
        maybeRefresh(payload.companyId);
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('entrepriseCompanyUpdated', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('entrepriseCompanyUpdated', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [token, user?.entreprise_id]);

  const value = {
    selectedSite,
    sites,
    multiSites,
    pointageInterSiteEnabled,
    pointageTerrainEnabled,
    paieEnabled,
    comptabilitePaieEnabled,
    salaryAdvancesEnabled,
    loading,
    selectSite,
    refreshSites,
    // Exposer les valeurs centralisées
    hasSiteManagement,
    assignedSiteId,
    canSeeGeneralized,
    isAdminRH,
    siteChanging // Exposer l'état de changement de site pour le skeleton
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
